import {
  forwardRef,
  useCallback,
  useRef,
  useEffect,
  KeyboardEvent,
} from "react";
import {
  assignRefs,
  cx,
  useAppVariant,
  useAutoResizedTextarea,
  useIsFocused,
} from "@jugl-web/utils";
import { PlainButton } from "@jugl-web/ui-components";
import { ReactComponent as SendIcon } from "./assets/send.svg";

export interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  /** Font size in pixels for the textarea text. */
  textSize?: number;
  /** When true, the textarea and send button are non-interactive. */
  isDisabled?: boolean;
  /** Custom class name for the container. */
  className?: string;
  /** Custom class name for the textarea. */
  inputClassName?: string;
}

export const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  (
    {
      value,
      onChange,
      onSend,
      placeholder = "Ask anything",
      textSize = 14,
      isDisabled = false,
      className,
      inputClassName,
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const { resize } = useAutoResizedTextarea(textareaRef);
    const { isMobile } = useAppVariant();

    useEffect(() => {
      resize();
    }, [value, resize]);

    const canSend = !isDisabled && value.trim().length > 0;

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (!isMobile && e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          if (canSend) {
            onSend();
          }
        }
      },
      [canSend, isMobile, onSend]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value);
      },
      [onChange]
    );

    const { isFocused, focusProps } = useIsFocused();

    return (
      <div
        className={cx(
          "flex w-full cursor-text items-center gap-2 rounded-xl border border-solid bg-white px-3 py-2 transition-colors",
          isFocused
            ? "border-dark-600"
            : "border-grey-300 hover:border-dark-600",
          className
        )}
        onClick={() => {
          textareaRef.current?.focus();
        }}
      >
        <textarea
          ref={assignRefs([ref, textareaRef])}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cx(
            "max-h-[150px] flex-1 resize-none border-none bg-transparent font-secondary leading-normal text-dark outline-none placeholder:text-[#707577]",
            inputClassName
          )}
          style={{ fontSize: textSize }}
          rows={1}
          {...focusProps}
        />
        <PlainButton
          onClick={onSend}
          disabled={!canSend}
          className={cx(
            "flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center self-start rounded-lg border-none bg-grey-100 transition-colors",
            {
              "hover:bg-grey-200 active:bg-grey-300": canSend,
              "cursor-not-allowed": !canSend,
            }
          )}
        >
          <SendIcon />
        </PlainButton>
      </div>
    );
  }
);

ChatInput.displayName = "ChatInput";
